import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { useCallback, useMemo } from "react";

import { isConversationToken } from "../utils";

import { ConversationModel } from "./ConversationModel";
import { useMessengerContext } from "./MessengerContext";
import { useMessengerStoreMainStreamFetchState } from "./useMessengerStoreMainStreamFetchState";

export const useMessengerStoreConversationIdentification = () => {
  const { mainStreamFetchState } = useMessengerStoreMainStreamFetchState();
  const user = useMessengerContext(({ user }) => user);

  const getConversationByIdentifier = useCallback(
    (
      conversationIdentifier: string | undefined
    ): ConversationModel | undefined => {
      if (!user || !mainStreamFetchState) {
        return;
      }

      if (!conversationIdentifier) {
        return undefined;
      }

      if (isConversationToken(conversationIdentifier)) {
        return mainStreamFetchState.conversationByConversationToken.get(
          conversationIdentifier
        );
      }

      // Support for deprecated v2 email links that do not pass in brand/retailer tokens
      const conversation =
        user?.type === IUser.Type.BRAND_USER
          ? mainStreamFetchState.conversationByRetailerToken.get(
              conversationIdentifier
            )
          : mainStreamFetchState.conversationByBrandToken.get(
              conversationIdentifier
            );
      if (conversation) {
        return conversation;
      }

      return mainStreamFetchState.conversationByUserToken.get(
        conversationIdentifier
      );
    },
    [user, mainStreamFetchState]
  );

  const hasConversationWithIdentifier = useCallback(
    (identifier: string): boolean => {
      return getConversationByIdentifier(identifier) !== undefined;
    },
    [getConversationByIdentifier]
  );

  return useMemo(
    () => ({
      getConversationByIdentifier,
      hasConversationWithIdentifier,
    }),
    [getConversationByIdentifier, hasConversationWithIdentifier]
  );
};
